import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Seo from "@/components/Seo";
import ContactBar from "@/components/ContactBar";
import ContactForm from "@/components/ContactForm";
import FAQSection from "@/components/FAQSection";
import Footer from "@/components/Footer";
import BookCallButton from "@/components/BookCall";
import ServiceIcon from "@/components/ServiceIcons";
import Reveal from "@/components/Reveal";
import { sz, txt, useContent } from "@/content/ContentContext";
import { buildServicesHubFaqs, resolveFaqs } from "@/content/faqData";
import { PAGE_META, breadcrumbSchema } from "@/content/seoData";

const Services = () => {
  const c = useContent();
  const o = c.services.overview;
  const umbrellas = c.services.umbrellas || [];

  return (
    <div className="App">
      <Seo
        title={PAGE_META.services.title}
        description={PAGE_META.services.description}
        path="/services"
        schemas={[breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Services", path: "/services" },
        ])]}
      />
      <Header />
      <main>
        <section className="svcpage-hero">
          <Reveal className="svcpage-hero-inner" y={18}>
            <h1
              className="svcpage-title"
              style={sz(o.titleStart)}
              data-testid="page-title"
            >
              {txt(o.titleStart)}{" "}
              <span className="text-green" style={sz(o.titleAccent)}>
                {txt(o.titleAccent)}
              </span>
            </h1>
            <p className="svcpage-intro" style={sz(o.intro)}>
              {txt(o.intro)}
            </p>
            <div className="svcpage-nav">
              {umbrellas.map((umbrella) => (
                <a
                  key={umbrella.slug}
                  href={`#${umbrella.slug}`}
                  className="svcpage-chip"
                  style={sz(umbrella.title)}
                  data-testid={`umbrella-chip-${umbrella.slug}`}
                >
                  {txt(umbrella.title)}
                </a>
              ))}
            </div>
            <BookCallButton
              className="btn-outline-light"
              testId="services-hero-cta"
            />
            <a
              href="#message"
              className="alt-contact-link on-dark"
              style={sz(c.global.altContactLabel)}
              data-testid="services-hero-alt-contact"
            >
              {txt(c.global.altContactLabel)}
            </a>
          </Reveal>
        </section>

        <section className="svcs-list">
          {umbrellas.map((umbrella) => (
            <Reveal
              as="article"
              key={umbrella.slug}
              id={umbrella.slug}
              className="svcs-block"
              data-testid={`umbrella-block-${umbrella.slug}`}
              y={26}
            >
              <div className="svc-section-side">
                <span className="svcs-umbrella-icon">
                  <ServiceIcon name={umbrella.icon} />
                </span>
              </div>
              <div className="svcs-block-main">
                <h2 className="svcs-block-title" style={sz(umbrella.title)}>
                  {txt(umbrella.title)}
                </h2>
                <p className="svcs-umbrella-hook" style={sz(umbrella.hook)}>
                  {txt(umbrella.hook)}
                </p>
                {(umbrella.paragraphs || []).map((paragraph, index) => (
                  <p className="svcs-block-desc" key={index}>
                    {paragraph}
                  </p>
                ))}
                <div className="svcs-sub-grid">
                  {(umbrella.services || []).map((service) => (
                    <Link
                      key={service.slug}
                      to={`/services/${service.slug}`}
                      className="svcs-sub-item"
                      data-testid={`service-card-${service.slug}`}
                    >
                      <span className="svcs-sub-icon">
                        <ServiceIcon name={service.icon} />
                      </span>
                      <span
                        className="svcs-sub-label"
                        style={sz(service.title)}
                      >
                        {txt(service.title)}
                      </span>
                      <span
                        className="svcs-sub-short"
                        style={sz(service.teaser)}
                      >
                        {txt(service.teaser)}
                      </span>
                      <span
                        className="svcs-sub-more"
                        style={sz(o.learnMoreLabel)}
                      >
                        {txt(o.learnMoreLabel)}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </section>

        <Reveal as="section" className="cta-banner" y={20}>
          <div className="cta-banner-text">
            <h3 style={sz(o.banner.heading)}>{txt(o.banner.heading)}</h3>
            <p style={sz(o.banner.text)}>{txt(o.banner.text)}</p>
          </div>
          <div className="cta-banner-actions">
            <BookCallButton className="btn-outline-light" testId="banner-cta" />
            <a
              href="#message"
              className="alt-contact-link on-dark"
              style={sz(c.global.altContactLabel)}
              data-testid="banner-alt-contact"
            >
              {txt(c.global.altContactLabel)}
            </a>
          </div>
        </Reveal>
      </main>
      <FAQSection items={resolveFaqs(c, "services", buildServicesHubFaqs(c))} />
      <ContactForm />
      <ContactBar />
      <Footer />
    </div>
  );
};

export default Services;
